import { faCalculator, faCodeMerge, faEuro } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import Input from "components/common/form/inputs/Input";
import If from "components/common/If";
import Translate from "components/common/Translate";
import { uuid } from "config/app-constants";
import { DropdownInputPropsType } from "types/form-types";
import { BookingFormDataQuery } from "types/graphql";
import { DateComparisons, DateFormats, DateUtil } from "utilities/date-utilities";
import { BookingFormChannelModelType } from "./BookingFormChannel";

type FormRoomType = NonNullable<BookingFormDataQuery['rooms']>['items'][0];
type FormRoomTypeType = FormRoomType['roomType'];

export type DetailsType = {
    dates: Date[]
    room?: FormRoomType
    price?: number | string,
    id: number | string
}

type BookingFormDetailsPropsType = {
    roomType?: FormRoomTypeType
    preferredRoom?: FormRoomType
    rooms: FormRoomType[]
    setDetails: (details: DetailsType[]) => void
    details: DetailsType[],
    channel?: BookingFormChannelModelType
}

type BookingFormDetailsItemPropsType = BookingFormDetailsPropsType & {
    value: DetailsType,
    onChange: (item: DetailsType, index: number) => void
    index: number
    rooms: FormRoomType[],
    onCheckboxClick: (date: Date, index: number) => void,
    onRemoveItem: (date: Date, index: number) => void
}

const BookingFormDetailItem = (props: BookingFormDetailsItemPropsType) => {
    const roomConfig: DropdownInputPropsType<FormRoomType> = {
        name: 'preferredRoom',
        dataSource: {
            data(searchText) {
                let theRooms = [...props.rooms];
                return searchText ? theRooms.filter(r => r.name.toLowerCase().includes(searchText.toLowerCase())) : theRooms;
            },
        },
        getItemId: i => i?.id!,
        view: {
            inputView: i => i ? `${i.name} (${i.roomType.name})` : '',
        }
    }

    return (
        <div>
            <div className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                <div className="my-auto pr-2 pl-1">
                    {props.value.dates?.map((date, index) => {
                        let dateStr = DateUtil.format(date, DateFormats.SHORT);
                        return <Button key={`${props.value.id}-${dateStr}`} 
                            name={`booking-form-detail-item-${DateUtil.format(date, DateFormats.ISO)}`}
                            className="m-1" 
                            onClick={() => props.onRemoveItem(date, props.index)} title={dateStr}
                            theme="link"
                            disabled={!index}
                        >
                            <span>{dateStr}</span>
                        </Button>
                    })}
                </div>
                <div className="flex my-auto">
                    <div className="my-auto">
                        <If condition={props.index}>
                            <Button
                                onClick={() => props.onCheckboxClick(props.value.dates[props.value.dates.length - 1], props.index)}
                                title="link"
                            >
                                <FontAwesomeIcon icon={faCodeMerge} />
                            </Button>
                        </If>
                    </div>
                    <DropdownInput
                        {...roomConfig}
                        label={undefined}
                        value={props.value.room}
                        onChange={(value) => props.onChange({ ...props.value, room: value }, props.index)}
                    />
                    <Input
                        name="pricePerNight"
                        placeholder="fields.pricePerNight.name"
                        required
                        min={0}
                        type="number"
                        value={props.value.price?.toString() || ''}
                        onChange={(value) => props.onChange({ ...props.value, price: value }, props.index)}
                        suffix={[
                            { icon: faEuro, name: 'euro', title: 'Euro' },
                            {
                                icon: faCalculator,
                                name: 'calculate',
                                title: 'fields.calculate.name',
                                onClick: () => {
                                    let price = props.rooms[0].roomType.defaultPrice;
                                    if (props.roomType?.name && props.channel?.channel?.roomTypeDefultPrices?.items?.length) {
                                        price = props.channel.channel.roomTypeDefultPrices.items.find(x => x.roomType.name === props.roomType!.name)?.defaultPrice || props.rooms[0].roomType.defaultPrice;
                                    }
                                    props.onChange({ ...props.value, price }, props.index);
                                }
                            }
                        ]}
                    />
                </div>
            </div>
        </div>
    );
}

const BookingFormDetails = (props: BookingFormDetailsPropsType) => {
    const onItemChange = (item: DetailsType, index: number) => {
        const newDetails = [...props.details];
        newDetails[index] = item;
        props.setDetails(newDetails);
    }

    function onRemoveItem(date: Date, index: number) {
        if(index >= props.details.length) {
            return;
        }

        let newDetails: DetailsType[] = []; 
        for (let i = 0; i < props.details.length; i++) {
            let item = {
                ...props.details[i]
            };
            if(i === index) {
                const firstHalfDates = item.dates.filter(d => DateUtil.compareOnAsIsoDate(d, date, DateComparisons.LOWER));
                const secundHalfDates = item.dates.filter(d => DateUtil.compareOnAsIsoDate(d, date, DateComparisons.GREATER_OR_EQUAL));
                if(firstHalfDates.length) {
                    newDetails.push({
                        ...item,
                        dates: firstHalfDates
                    });
                }

                if(secundHalfDates.length) {
                    newDetails.push({
                        ...item,
                        id: uuid(),
                        dates: secundHalfDates
                    });
                }
            } else {
                newDetails.push(item);
            }
        }

        console.log(newDetails.length, props.details.length);
        props.setDetails(newDetails);
    }

    function onCheckboxClick(date: Date, index: number) {
        if(index === 0 || index >= props.details.length) {
            return;
        }
        
        const newDetails = [];
        for (let i = 0; i < props.details.length; i++) {
            console.log('the e', i);
            let item = {
                ...props.details[i]
            };
            if (i === index - 1) {
                i++;
                item = props.details[index];
                item.dates = [...props.details[index - 1].dates, ...item.dates];
            }

            newDetails.push(item);
        }
        console.log(newDetails.length, props.details.length);

        props.setDetails(newDetails);
    }

    return (
        <div>
            <If condition={!props.roomType || !props.details.length}>
                <div className="w-full text-center p-2">
                    <Translate i18nKey='accommodation.booking.form.selectRoomTypeAndDate' />
                </div>
            </If>
            <If condition={props.roomType && props.details.length}>
                <div>
                    {props.details.map((detail, index) => (
                        <BookingFormDetailItem
                            key={detail.id}
                            {...props}
                            value={detail}
                            index={index}
                            channel={props.channel}
                            onChange={onItemChange}
                            onCheckboxClick={onCheckboxClick}
                            onRemoveItem={onRemoveItem}
                        />
                    ))}
                </div>
            </If>
        </div>
    );
};

export default BookingFormDetails;
